/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

export interface SDGTarget {
  code: string;
  shortName: string;
  hidden?: boolean;
}

export interface SDGGoal {
  code: string;
  shortName: string;
  targets: SDGTarget[];
  hidden?: boolean;
}

export const sdgMap: SDGGoal[] = [
  {
    'code': '1',
    'shortName': 'No Poverty',
    'targets': [
      {
        'code': '1.1',
        'shortName': 'Eradicate Extreme Poverty'
      },
      {
        'code': '1.2',
        'shortName': 'Reduce Poverty By At Least 50%'
      },
      {
        'code': '1.3',
        'shortName': 'Implement Social Protection Systems'
      },
      {
        'code': '1.4',
        'shortName': 'Equal Rights To Ownership, Basic Services, Technology And Economic Resources'
      },
      {
        'code': '1.5',
        'shortName': 'Build Resilience To Environmental, Economic And Social Disasters'
      },
      {
        'code': '1.a',
        'shortName': 'Mobilize Resources To Implement Policies To End Poverty',
      },
      {
        'code': '1.b',
        'shortName': 'Create Pro-Poor And Gender-Sensitive Policy Frameworks',
      }
    ]
  },
  {
    'code': '2',
    'shortName': 'Zero Hunger',
    'targets': [
      {
        'code': '2.1',
        'shortName': 'Universal Access To Safe And Nutritious Food'
      },
      {
        'code': '2.2',
        'shortName': 'End All Forms Of Malnutrition'
      },
      {
        'code': '2.3',
        'shortName': 'Double The Productivity And Incomes Of Small-Scale Food Producers'
      },
      {
        'code': '2.4',
        'shortName': 'Sustainable Food Production And Resilient Agricultural Practices'
      },
      {
        'code': '2.5',
        'shortName': 'Maintain The Genetic Diversity In Food Production'
      },
      {
        'code': '2.a',
        'shortName': 'Invest In Rural Infrastructure, Agricultural Research, Technology And Gene Banks',
      },
      {
        'code': '2.b',
        'shortName': 'Prevent Agricultural Trade Restrictions, Market Distortions And Export Subsidies',
      },
      {
        'code': '2.c',
        'shortName': 'Ensure Stable Food Commodity Markets And Timely Access To Information',
      }
    ]
  },
  {
    'code': '3',
    'shortName': 'Good Health And Well-Being',
    'targets': [
      {
        'code': '3.1',
        'shortName': 'Reduce Maternal Mortality'
      },
      {
        'code': '3.2',
        'shortName': 'End All Preventable Deaths Under 5 Years Of Age'
      },
      {
        'code': '3.3',
        'shortName': 'Fight Communicable Diseases'
      },
      {
        'code': '3.4',
        'shortName': 'Reduce Mortality From Non-Communicable Diseases And Promote Mental Health'
      },
      {
        'code': '3.5',
        'shortName': 'Prevent And Treat Substance Abuse'
      },
      {
        'code': '3.6',
        'shortName': 'Reduce Road Injuries And Deaths'
      },
      {
        'code': '3.7',
        'shortName': 'Universal Access To Sexual And Reproductive Care, Family Planning And Education'
      },
      {
        'code': '3.8',
        'shortName': 'Achieve Universal Health Coverage'
      },
      {
        'code': '3.9',
        'shortName': 'Reduce Illnesses And Death From Hazardous Chemicals And Pollution'
      },
      {
        'code': '3.a',
        'shortName': 'Implement The Who Framework Convention On Tobacco Control',
      },
      {
        'code': '3.b',
        'shortName': 'Support Research, Development And Universal Access To Affordable Vaccines And Medicines',
      },
      {
        'code': '3.c',
        'shortName': 'Increase Health Financing And Support Health Workforce In Developing Countries',
      },
      {
        'code': '3.d',
        'shortName': 'Improve Early Warning Systems For Global Health Risks',
      }
    ]
  },
  {
    'code': '4',
    'shortName': 'Quality Education',
    'targets': [
      {
        'code': '4.1',
        'shortName': 'Free Primary And Secondary Education'
      },
      {
        'code': '4.2',
        'shortName': 'Equal Access To Quality Pre-Primary Education'
      },
      {
        'code': '4.3',
        'shortName': 'Equal Access To Affordable Technical, Vocational And Higher Education'
      },
      {
        'code': '4.4',
        'shortName': 'Increase The Number Of People With Relevant Skills For Financial Success'
      },
      {
        'code': '4.5',
        'shortName': 'Eliminate All Discrimination In Education'
      },
      {
        'code': '4.6',
        'shortName': 'Universal Literacy And Numeracy'
      },
      {
        'code': '4.7',
        'shortName': 'Education For Sustainable Development And Global Citizenship'
      },
      {
        'code': '4.a',
        'shortName': 'Build And Upgrade Inclusive And Safe Schools',
      },
      {
        'code': '4.b',
        'shortName': 'Expand Higher Education Scholarships For Developing Countries',
      },
      {
        'code': '4.c',
        'shortName': 'Increase The Supply Of Qualified Teachers In Developing Countries',
      }
    ]
  },
  {
    'code': '5',
    'shortName': 'Gender Equality',
    'targets': [
      {
        'code': '5.1',
        'shortName': 'End Discrimination Against Women And Girls'
      },
      {
        'code': '5.2',
        'shortName': 'End All Violence Against And Exploitation Of Women And Girls'
      },
      {
        'code': '5.3',
        'shortName': 'Eliminate Forced Marriages And Genital Mutilation'
      },
      {
        'code': '5.4',
        'shortName': 'Value Unpaid Care And Promote Shared Domestic Responsibilities'
      },
      {
        'code': '5.5',
        'shortName': 'Ensure Full Participation In Leadership And Decision-Making'
      },
      {
        'code': '5.6',
        'shortName': 'Universal Access To Reproductive Health And Rights'
      },
      {
        'code': '5.a',
        'shortName': 'Equal Rights To Economic Resources, Property Ownership And Financial Services',
      },
      {
        'code': '5.b',
        'shortName': 'Promote Empowerment Of Women Through Technology',
      },
      {
        'code': '5.c',
        'shortName': 'Adopt And Strengthen Policies And Enforceable Legislation For Gender Equality',
      }
    ]
  },
  {
    'code': '6',
    'shortName': 'Clean Water And Sanitation',
    'targets': [
      {
        'code': '6.1',
        'shortName': 'Safe And Affordable Drinking Water'
      },
      {
        'code': '6.2',
        'shortName': 'End Open Defecation And Provide Access To Sanitation And Hygiene'
      },
      {
        'code': '6.3',
        'shortName': 'Improve Water Quality, Wastewater Treatment And Safe Reuse'
      },
      {
        'code': '6.4',
        'shortName': 'Increase Water-Use Efficiency And Ensure Freshwater Supplies'
      },
      {
        'code': '6.5',
        'shortName': 'Implement Integrated Water Resources Management'
      },
      {
        'code': '6.6',
        'shortName': 'Protect And Restore Water-Related Ecosystems'
      },
      {
        'code': '6.a',
        'shortName': 'Expand Water And Sanitation Support To Developing Countries',
      },
      {
        'code': '6.b',
        'shortName': 'Support Local Engagement In Water And Sanitation Management',
      }
    ]
  },
  {
    'code': '7',
    'shortName': 'Affordable And Clean Energy',
    'targets': [
      {
        'code': '7.1',
        'shortName': 'Universal Access To Modern Energy'
      },
      {
        'code': '7.2',
        'shortName': 'Increase Global Percentage Of Renewable Energy'
      },
      {
        'code': '7.3',
        'shortName': 'Double The Improvement In Energy Efficiency'
      },
      {
        'code': '7.a',
        'shortName': 'Promote Access To Research, Technology And Investments In Clean Energy',
      },
      {
        'code': '7.b',
        'shortName': 'Expand And Upgrade Energy Services For Developing Countries',
      }
    ]
  },
  {
    'code': '8',
    'shortName': 'Decent Work And Economic Growth',
    'targets': [
      {
        'code': '8.2',
        'shortName': 'Sustainable Economic Growth'
      },
      {
        'code': '8.3',
        'shortName': 'Diversify, Innovate And Upgrade For Economic Productivity'
      },
      {
        'code': '8.4',
        'shortName': 'Promote Policies To Support Job Creation And Growing Enterprises'
      },
      {
        'code': '8.5',
        'shortName': 'Improve Resource Efficiency In Consumption And Production'
      },
      {
        'code': '8.6',
        'shortName': 'Full Employment And Decent Work With Equal Pay'
      },
      {
        'code': '8.7',
        'shortName': 'Promote Youth Employment, Education And Training'
      },
      {
        'code': '8.1',
        'shortName': 'End Modern Slavery, Trafficking And Child Labour'
      },
      {
        'code': '8.8',
        'shortName': 'Protect Labour Rights And Promote Safe Working Environments'
      },
      {
        'code': '8.9',
        'shortName': 'Promote Beneficial And Sustainable Tourism'
      },
      {
        'code': '8.10',
        'shortName': 'Universal Access To Banking, Insurance And Financial Services'
      },
      {
        'code': '8.a',
        'shortName': 'Increase Aid For Trade Support',
      },
      {
        'code': '8.b',
        'shortName': 'Develop A Global Youth Employment Strategy',
      }
    ]
  },
  {
    'code': '9',
    'shortName': 'Industry, Innovation And Infrastructure',
    'targets': [
      {
        'code': '9.1',
        'shortName': 'Develop Sustainable, Resilient And Inclusive Infrastructures'
      },
      {
        'code': '9.2',
        'shortName': 'Promote Inclusive And Sustainable Industrialization'
      },
      {
        'code': '9.3',
        'shortName': 'Increase Access To Financial Services And Markets'
      },
      {
        'code': '9.4',
        'shortName': 'Upgrade All Industries And Infrastructures For Sustainability'
      },
      {
        'code': '9.5',
        'shortName': 'Enhance Research And Upgrade Industrial Technologies'
      },
      {
        'code': '9.a',
        'shortName': 'Facilitate Sustainable Infrastructure Development For Developing Countries',
      },
      {
        'code': '9.b',
        'shortName': 'Support Domestic Technology Development And Industrial Diversification',
      },
      {
        'code': '9.c',
        'shortName': 'Universal Access To Information And Communications Technology',
      }
    ]
  },
  {
    'code': '10',
    'shortName': 'Reduced Inequalities',
    'targets': [
      {
        'code': '10.1',
        'shortName': 'Reduce Income Inequalities'
      },
      {
        'code': '10.2',
        'shortName': 'Promote Universal Social, Economic And Political Inclusion'
      },
      {
        'code': '10.3',
        'shortName': 'Ensure Equal Opportunities And End Discrimination'
      },
      {
        'code': '10.4',
        'shortName': 'Adopt Fiscal And Social Policies That Promotes Equality'
      },
      {
        'code': '10.5',
        'shortName': 'Improved Regulation Of Global Financial Markets And Institutions'
      },
      {
        'code': '10.6',
        'shortName': 'Enhanced Representation For Developing Countries In Financial Institutions'
      },
      {
        'code': '10.7',
        'shortName': 'Responsible And Well-Managed Migration Policies'
      },
      {
        'code': '10.a',
        'shortName': 'Special And Differential Treatment For Developing Countries',
      },
      {
        'code': '10.b',
        'shortName': 'Encourage Development Assistance And Investment In Least Developed Countries',
      },
      {
        'code': '10.c',
        'shortName': 'Reduce Transaction Costs For Migrant Remittances',
      }
    ]
  },
  {
    'code': '11',
    'shortName': 'Sustainable Cities And Communities',
    'targets': [
      {
        'code': '11.1',
        'shortName': 'Safe And Affordable Housing'
      },
      {
        'code': '11.2',
        'shortName': 'Affordable And Sustainable Transport Systems'
      },
      {
        'code': '11.3',
        'shortName': 'Inclusive And Sustainable Urbanization'
      },
      {
        'code': '11.4',
        'shortName': 'Protect The World’s Cultural And Natural Heritage'
      },
      {
        'code': '11.5',
        'shortName': 'Reduce The Adverse Effects Of Natural Disasters'
      },
      {
        'code': '11.6',
        'shortName': 'Reduce The Environmental Impact Of Cities'
      },
      {
        'code': '11.7',
        'shortName': 'Provide Access To Safe And Inclusive Green And Public Spaces'
      },
      {
        'code': '11.a',
        'shortName': 'Strong National And Regional Development Planning',
      },
      {
        'code': '11.b',
        'shortName': 'Implement Policies For Inclusion, Resource Efficiency And Disaster Risk Reduction',
      },
      {
        'code': '11.c',
        'shortName': 'Support Least Developed Countries In Sustainable And Resilient Building',
      }
    ]
  },
  {
    'code': '12',
    'shortName': 'Responsible Consumption And Production',
    'targets': [
      {
        'code': '12.1',
        'shortName': 'Implement The 10-Year Sustainable Consumption And Production Framework'
      },
      {
        'code': '12.2',
        'shortName': 'Sustainable Management And Use Of Natural Resources'
      },
      {
        'code': '12.3',
        'shortName': 'Halve Global Per Capita Food Waste'
      },
      {
        'code': '12.4',
        'shortName': 'Responsible Management Of Chemicals And Waste'
      },
      {
        'code': '12.5',
        'shortName': 'Substantially Reduce Waste Generation'
      },
      {
        'code': '12.6',
        'shortName': 'Encourage Companies To Adopt Sustainable Practices And Sustainability Reporting'
      },
      {
        'code': '12.7',
        'shortName': 'Promote Sustainable Public Procurement Practices'
      },
      {
        'code': '12.8',
        'shortName': 'Promote Universal Understanding Of Sustainable Lifestyles'
      },
      {
        'code': '12.a',
        'shortName': 'Support Developing Countries\' Scientific And Technological Capacity For Sustainable CONSUMPTION AND PRODUCTION',
      },
      {
        'code': '12.b',
        'shortName': 'Develop And Implement Tools To Monitor Sustainable Tourism',
      },
      {
        'code': '12.c',
        'shortName': 'Remove Market Distortions That Encourage Wasteful Consumption',
      }
    ]
  },
  {
    'code': '13',
    'shortName': 'Climate Action',
    'targets': [
      {
        'code': '13.1',
        'shortName': 'Strengthen Resilience And Adaptive Capacity To Climate Related Disasters'
      },
      {
        'code': '13.2',
        'shortName': 'Integrate Climate Change Measures Into Policies And Planning'
      },
      {
        'code': '13.3',
        'shortName': 'Build Knowledge And Capacity To Meet Climate Change'
      },
      {
        'code': '13.a',
        'shortName': 'Implement The Un Framework Convention On Climate Change',
      },
      {
        'code': '13.b',
        'shortName': 'Promote Mechanisms To Raise Capacity For Planning And Management',
      }
    ]
  },
  {
    'code': '14',
    'shortName': 'Life Below Water',
    'targets': [
      {
        'code': '14.1',
        'shortName': 'Reduce Marine Pollution'
      },
      {
        'code': '14.6',
        'shortName': 'Protect And Restore Ecosystems'
      },
      {
        'code': '14.2',
        'shortName': 'Reduce Ocean Acidification'
      },
      {
        'code': '14.7',
        'shortName': 'Sustainable Fishing'
      },
      {
        'code': '14.3',
        'shortName': 'Conserve Coastal And Marine Areas'
      },
      {
        'code': '14.4',
        'shortName': 'Increase The Economic Benefits From Sustainable Use Of Marine Resources'
      },
      {
        'code': '14.5',
        'shortName': 'Support Small Scale Fishers',
      },
      {
        'code': '14.a',
        'shortName': 'End Subsidies Contributing To Overfishing',
      },
      {
        'code': '14.b',
        'shortName': 'Increase Scientific Knowledge, Research And Technology For Ocean Health',
      },
      {
        'code': '14.c',
        'shortName': 'Implement And Enforce International Sea Law',
      }
    ]
  },
  {
    'code': '15',
    'shortName': 'Life On Land',
    'targets': [
      {
        'code': '15.1',
        'shortName': 'Conserve And Restore Terrestrial And Freshwater Ecosystems'
      },
      {
        'code': '15.2',
        'shortName': 'End Deforestation And Restore Degraded Forests'
      },
      {
        'code': '15.3',
        'shortName': 'End Desertification And Restore Degraded Land'
      },
      {
        'code': '15.4',
        'shortName': 'Ensure Conservation Of Mountain Ecosystems'
      },
      {
        'code': '15.5',
        'shortName': 'Protect Biodiversity And Natural Habitats'
      },
      {
        'code': '15.6',
        'shortName': 'Promote Access To Genetic Resources And Fair Sharing Of The Benefits'
      },
      {
        'code': '15.7',
        'shortName': 'Eliminate Poaching And Trafficking Of Protected Species'
      },
      {
        'code': '15.8',
        'shortName': 'Prevent Invasive Alien Species On Land And In Water Ecosystems'
      },
      {
        'code': '15.9',
        'shortName': 'Integrate Ecosystem And Biodiversity In Governmental Planning'
      },
      {
        'code': '15.a',
        'shortName': 'Increase Financial Resources To Conserve And Sustainably Use Ecosystem And Biodiversity',
      },
      {
        'code': '15.b',
        'shortName': 'Finance And Incentivize Sustainable Forest Management',
      },
      {
        'code': '15.c',
        'shortName': 'Combat Global Poaching And Trafficking',
      }
    ]
  },
  {
    'code': '16',
    'shortName': 'Peace, Justice And Strong Institutions',
    'targets': [
      {
        'code': '16.1',
        'shortName': 'Reduce Violence Everywhere'
      },
      {
        'code': '16.2',
        'shortName': 'Protect Children From Abuse, Exploitation, Trafficking And Violence'
      },
      {
        'code': '16.3',
        'shortName': 'Promote The Rule Of Law And Ensure Equal Access To Justice'
      },
      {
        'code': '16.4',
        'shortName': 'Combat Organized Crime And Illicit Financial And Arms Flows'
      },
      {
        'code': '16.5',
        'shortName': 'Substantially Reduce Corruption And Bribery'
      },
      {
        'code': '16.6',
        'shortName': 'Develop Effective, Accountable And Transparent Institutions'
      },
      {
        'code': '16.7',
        'shortName': 'Ensure Responsive, Inclusive And Representative Decision-Making'
      },
      {
        'code': '16.8',
        'shortName': 'Strengthen The Participation In Global Governance'
      },
      {
        'code': '16.9',
        'shortName': 'Provide Universal Legal Identity'
      },
      {
        'code': '16.10',
        'shortName': 'Ensure Public Access To Information And Protect Fundamental Freedoms'
      },
      {
        'code': '16.a',
        'shortName': 'Strengthen National Institutions To Prevent Violence And Combat Terrorism And Crime',
      },
      {
        'code': '16.b',
        'shortName': 'Promote And Enforce Non-Discriminatory Laws And Policies',
      }
    ]
  },
  {
    'code': '17',
    'shortName': 'Partnership For The Goals',
    'targets': [
      {
        'code': '17.1',
        'shortName': 'Mobilize Resources To Improve Domestic Revenue Collection'
      },
      {
        'code': '17.2',
        'shortName': 'Implement All Development Assistance Commitments'
      },
      {
        'code': '17.3',
        'shortName': 'Mobilize Financial Resources For Developing Countries'
      },
      {
        'code': '17.4',
        'shortName': 'Assist Developing Countries In Attaining Debt Sustainability'
      },
      {
        'code': '17.5',
        'shortName': 'Invest In Least Developed Countries'
      },
      {
        'code': '17.6',
        'shortName': 'Knowledge Sharing And Cooperation For Access To Science, Technology And Innovation'
      },
      {
        'code': '17.7',
        'shortName': 'Promote Sustainable Technologies To Developing Countries'
      },
      {
        'code': '17.8',
        'shortName': 'Strengthen The Science, Technology And Innovation Capacity For Least Developed CountRIES'
      },
      {
        'code': '17.9',
        'shortName': 'Enhance SDG Capacity In Developing Countries'
      },
      {
        'code': '17.10',
        'shortName': 'Promote A Universal Trading System Under The Wto'
      },
      {
        'code': '17.11',
        'shortName': 'Increase The Exports Of Developing Countries'
      },
      {
        'code': '17.12',
        'shortName': 'Remove Trade Barriers For Least Developed Countries'
      },
      {
        'code': '17.13',
        'shortName': 'Enhance Global Macroeconomic Stability'
      },
      {
        'code': '17.14',
        'shortName': 'Enhance Policy Coherence For Sustainable Development'
      },
      {
        'code': '17.15',
        'shortName': 'Respect National Leadership To Implement Policies For The Sustainable Development GoALS'
      },
      {
        'code': '17.16',
        'shortName': 'Enhance The Global Partnership For Sustainable Development'
      },
      {
        'code': '17.17',
        'shortName': 'Encourage Effective Partnerships'
      },
      {
        'code': '17.18',
        'shortName': 'Enhance Availability Of Reliable Data'
      },
      {
        'code': '17.19',
        'shortName': 'Further Develop Measurements Of Progress'
      }
    ]
  }
]

export const getSDGShortTitle = (sdgCode: string | number) => {

  const [goalCode, targetCode] = String(sdgCode).split('.');

  const goal = sdgMap.find(({ code }) => code === goalCode);

  if (!goal) {
    return '';
  }

  if (!targetCode) {
    return goal.shortName;
  }

  const target = goal.targets.find(({ code }) => code === sdgCode);
  return target ? target.shortName : '';
}

export const getSDGTitle = ({ goalCode }: { goalCode: string }) => {
  return `Goal ${goalCode}: ${getSDGShortTitle(goalCode)}`;
};

export const getSDGTitleByCode = (sdgCode: string) => {
  const prefix = sdgCode.split('.').length > 1 ? 'Target' : 'Goal';
  return `${prefix} ${sdgCode} - ${getSDGShortTitle(sdgCode)}`;
}

/** prefix of "sdg/" and the code of the goal or target */
export const getSdgUtrCode = (code: string) => `sdg/${code}`;


export const isHidden = (sdgCode: string | number) => {
  const [goalCode, targetCode] = String(sdgCode).split('.');

  const goal = sdgMap.find(({ code }) => code === goalCode);

  if (!goal) {
    return true;
  }

  if (!targetCode) {
    return goal.hidden === true;
  }

  const target = goal.targets.find(({ code }) => code === sdgCode);
  return target?.hidden === true;
}
